import { useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';
import {
  useProjectParticipants,
  useAddParticipant,
  useRemoveParticipant,
} from '../../../api/hooks/useProjects';
import { useEmployees, useEmployeesByIds } from '../../../api/hooks/useEmployees';
import { useNotifications } from '../../../context/NotificationContext';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { apiErrorMessage } from '../../../lib/apiErrorMessage';
import { AppDialogTitle } from '../../../components/common/AppDialogTitle';

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
}

interface Participant {
  id: string;
  coworker_id: string;
  role: string;
  coworker_name?: string;
}

const ROLE_COLORS: Record<string, 'primary' | 'secondary' | 'info' | 'success' | 'default'> = {
  OWNER: 'primary',
  MANAGER: 'primary',
  ASSISTANT: 'secondary',
  OBSERVER: 'info',
  CONTRIBUTOR: 'success',
};

const ROLE_OPTIONS: Array<{ value: string; labelDe: string }> = [
  { value: 'MANAGER', labelDe: 'Projektleitung' },
  { value: 'OWNER', labelDe: 'Eigentümer' },
  { value: 'ASSISTANT', labelDe: 'Assistenz' },
  { value: 'CONTRIBUTOR', labelDe: 'Mitarbeit' },
  { value: 'OBSERVER', labelDe: 'Beobachter' },
];

export function ParticipantSection({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();

  const { data, isLoading } = useProjectParticipants(projectId);
  const addMutation = useAddParticipant();
  const removeMutation = useRemoveParticipant();

  const { data: employeesData } = useEmployees({ limit: 100 });
  const employees = useMemo(() => {
    const resp = employeesData as unknown as { data?: Employee[] } | undefined;
    return resp?.data ?? [];
  }, [employeesData]);

  const participants = (data as unknown as { data?: Participant[] } | undefined)?.data ?? [];
  const { map: resolvedNames } = useEmployeesByIds(participants.map((p) => p.coworker_id));

  const displayName = (p: Participant): string => {
    if (p.coworker_name) return p.coworker_name;
    const emp = resolvedNames.get(p.coworker_id);
    if (emp) {
      const composed = `${emp.first_name} ${emp.last_name}`.trim();
      return composed || emp.display_name || p.coworker_id;
    }
    return p.coworker_id;
  };

  const [addOpen, setAddOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [role, setRole] = useState('MANAGER');
  const [deleteTarget, setDeleteTarget] = useState<Participant | null>(null);

  // Filter out already added participants
  const availableEmployees = useMemo(() => {
    const existingIds = new Set(participants.map((p) => p.coworker_id));
    return employees.filter((e) => !existingIds.has(e.id));
  }, [employees, participants]);

  const handleAdd = async () => {
    if (!selectedEmployee) {
      notify(tc('errors.requiredFields', 'Pflichtfelder ausfüllen'), 'warning');
      return;
    }
    try {
      await addMutation.mutateAsync({
        projectId,
        data: { coworker_id: selectedEmployee.id, role },
      });
      notify(tc('status.saved', 'Gespeichert'), 'success');
      setAddOpen(false);
      setSelectedEmployee(null);
      setRole('MANAGER');
    } catch (err) {
      notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error');
    }
  };

  const handleRemove = async () => {
    if (!deleteTarget) return;
    try {
      await removeMutation.mutateAsync({ projectId, participantId: deleteTarget.id });
      notify(tc('status.saved', 'Entfernt'), 'success');
    } catch (err) {
      notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error');
    }
    setDeleteTarget(null);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{t('participants.title', 'Teilnehmer')}</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
          {t('participants.add', 'Hinzufügen')}
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : participants.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('participants.empty', 'Keine Teilnehmer zugeordnet')}
        </Typography>
      ) : (
        <List disablePadding>
          {participants.map((p) => (
            <ListItem
              key={p.id}
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  color="error"
                  onClick={() => setDeleteTarget(p)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemAvatar>
                <Avatar><PersonIcon /></Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={displayName(p)}
                secondary={
                  <Chip
                    label={t(`participants.roles.${p.role}`, p.role)}
                    color={ROLE_COLORS[p.role] ?? 'default'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                }
              />
            </ListItem>
          ))}
        </List>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <AppDialogTitle>{t('participants.add', 'Teilnehmer hinzufügen')}</AppDialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={availableEmployees}
              getOptionLabel={(opt) => `${opt.first_name} ${opt.last_name}`}
              value={selectedEmployee}
              onChange={(_, selected) => setSelectedEmployee(selected)}
              filterOptions={(options, { inputValue }) => {
                const lower = inputValue.toLowerCase();
                return options.filter((e) =>
                  `${e.first_name} ${e.last_name}`.toLowerCase().includes(lower),
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t('workLogs.coworker', 'Mitarbeiter')}
                  placeholder={t('participants.searchEmployee', 'Mitarbeiter suchen...')}
                />
              )}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
            />
            <FormControl fullWidth>
              <InputLabel>{t('participants.roleLabel', 'Rolle')}</InputLabel>
              <Select
                value={role}
                label={t('participants.roleLabel', 'Rolle')}
                onChange={(e) => setRole(e.target.value)}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {t(`participants.roles.${opt.value}`, opt.labelDe)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>{tc('actions.cancel', 'Abbrechen')}</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={addMutation.isPending || !selectedEmployee}
            startIcon={addMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {tc('actions.save', 'Speichern')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={tc('actions.delete', 'Löschen')}
        message={t('participants.confirmRemove', 'Teilnehmer wirklich entfernen?')}
        confirmColor="error"
        confirmLabel={tc('actions.delete', 'Entfernen')}
        onConfirm={handleRemove}
        onCancel={() => setDeleteTarget(null)}
        loading={removeMutation.isPending}
      />
    </Paper>
  );
}
